import { Direction, Status } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { ThreadReadStatus } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { ThreadItem } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { getThreadItemCreatedAt, getThreadItemId, isSmsMessageItem } from '../../../utils/thread-items-utils';
import { EndpointQueryData } from '../types';

type UpsertThreadItemOntoGetUnifiedThreadQueryArgs = {
  query: EndpointQueryData<'GetUnifiedThread'>;
  newThreadItem: ThreadItem;
  matchingThreadItemId: string;
};

/**
 * Updates a GetUnifiedThread query's cached data by upserting a new ThreadItem into the appropriate position
 * within the thread's items list, maintaining chronological order.
 *
 * @param query - A tuple containing the query key and query data for the GetUnifiedThread endpoint
 * @param newThreadItem - The new ThreadItem to be upserted into the thread
 * @param matchingThreadItemId - The ID of any existing ThreadItem that should be replaced by the new ThreadItem
 *
 * @returns A new EndpointQueryData tuple with updated pages containing the upserted ThreadItem
 */
export const upsertThreadItemOntoGetUnifiedThreadQuery = ({
  query: [queryKey, queryData],
  newThreadItem,
  matchingThreadItemId,
}: UpsertThreadItemOntoGetUnifiedThreadQueryArgs): EndpointQueryData<'GetUnifiedThread'> => {
  const request = queryKey[2];
  const newThreadItemCreatedAt = new Date(getThreadItemCreatedAt(newThreadItem)).getTime();

  const hasLatestItem =
    !request.filter?.focusCreatedAt || !request.filter.focusThreadItem || queryData.pages[0]?.newerPageToken === '';

  const hasNewerOrMatchingItem = queryData.pages.some(
    (page) =>
      page.thread.items.some((item) => new Date(getThreadItemCreatedAt(item)).getTime() > newThreadItemCreatedAt) ||
      page.thread.items.some((item) => getThreadItemId(item) === matchingThreadItemId)
  );

  const pagesMaxIndex = queryData.pages.length - 1;
  const lastPageItemsMaxIndex = pagesMaxIndex >= 0 ? queryData.pages[pagesMaxIndex]!.thread.items.length - 1 : -1;
  const lastItemIsMatch =
    lastPageItemsMaxIndex >= 0
      ? getThreadItemId(queryData.pages[pagesMaxIndex]!.thread.items[lastPageItemsMaxIndex]!) === matchingThreadItemId
      : false;

  const { newPages } = queryData.pages.reduce<{
    hasUpserted: boolean;
    newPages: typeof queryData.pages;
  }>(
    (pagesAcc, page) => {
      const { newItems, hasUpserted } = page.thread.items.reduce<{
        hasUpserted: boolean;
        newItems: ThreadItem[];
      }>(
        (itemsAcc, item) => {
          const itemCreatedAt = new Date(getThreadItemCreatedAt(item)).getTime();

          const mode = itemsAcc.hasUpserted
            ? 'filter'
            : hasLatestItem || hasNewerOrMatchingItem
            ? 'upsert'
            : 'cannot-upsert';

          switch (mode) {
            case 'upsert': {
              if (itemCreatedAt < newThreadItemCreatedAt && getThreadItemId(item) !== matchingThreadItemId) {
                itemsAcc.newItems.push(newThreadItem);
                itemsAcc.newItems.push(item);
                itemsAcc.hasUpserted = true;
              } else if (lastItemIsMatch && getThreadItemId(item) === matchingThreadItemId) {
                itemsAcc.newItems.push(newThreadItem);
              } else if (getThreadItemId(item) !== matchingThreadItemId) {
                itemsAcc.newItems.push(item);
              }
              break;
            }
            case 'filter': {
              if (getThreadItemId(item) !== matchingThreadItemId) {
                itemsAcc.newItems.push(item);
              }
              break;
            }
            case 'cannot-upsert': {
              itemsAcc.newItems.push(item);
              break;
            }
          }

          return itemsAcc;
        },
        {
          hasUpserted: pagesAcc.hasUpserted,
          newItems: [],
        }
      );

      pagesAcc.hasUpserted = pagesAcc.hasUpserted || hasUpserted;
      // Preserve existing thread data but update items. If we upserted an SMS item,
      // ensure the thread.status reflects the newest SMS (so inbound unread stays 'new').
      const updatedThread = {
        ...page.thread,
        items: newItems,
      };

      let newStatus = updatedThread.status;
      let newIsReplied = updatedThread.isReplied;

      // update the thread-level status when the inserted item is new.
      const insertedIndex = newItems.findIndex((item) => getThreadItemId(item) === getThreadItemId(newThreadItem));

      if (insertedIndex === 0) {
        // Prefer the incoming/new thread item as the source of truth for status
        // calculation. Fall back to the newest SMS item on the page if necessary.
        const smsSource = isSmsMessageItem(newThreadItem)
          ? newThreadItem
          : newItems.find((item) => isSmsMessageItem(item));
        if (smsSource && isSmsMessageItem(smsSource)) {
          const sms = smsSource.smsMessage!;

          newIsReplied = sms.direction === Direction.DIRECTION_OUTBOUND && !sms.autogeneratedBy;

          if (sms.status === Status.STATUS_ERROR) {
            newStatus = ThreadReadStatus.THREAD_READ_STATUS_ERROR;
          } else if (sms.direction === Direction.DIRECTION_INBOUND && sms.status !== Status.STATUS_READ) {
            newStatus = ThreadReadStatus.THREAD_READ_STATUS_NEW;
          } else if (!sms.autogeneratedBy) {
            newStatus = ThreadReadStatus.THREAD_READ_STATUS_READ;
          }
        }
      }

      pagesAcc.newPages.push({
        ...page,
        thread: {
          ...updatedThread,
          status: newStatus,
          isReplied: newIsReplied,
        },
      });

      return pagesAcc;
    },
    { hasUpserted: false, newPages: [] }
  );

  return [
    queryKey,
    {
      ...queryData,
      pages: newPages,
    },
  ];
};
